import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";

/*
  STEPHEN AI: chat page

  Same dependencies as before: React, react-markdown, Tailwind CSS.
  Same backend contract: POST { question, role, thread_id } -> { answer, intent, grounding_notes }.

  What changed (UX / engineering):
  - ChatGPT/Claude-style layout: persistent collapsible sidebar on desktop, drawer on mobile,
    one centred reading column (max ~48rem, ~70ch line length), user bubbles on the right,
    assistant answers flat on the canvas with no card chrome.
  - Composer is a floating pill: auto-growing textarea, send button that becomes a Stop button
    while a request is in flight, Enter to send, Shift+Enter for a new line, IME-safe.
  - Smart scrolling: follows the answer only while you are at the bottom; a "jump to latest"
    button appears when you scroll up. Driven by ResizeObserver, no scroll fighting.
  - Regenerate now replaces the last answer instead of appending a duplicate question.
  - Answer reveal animation (respects prefers-reduced-motion).
  - Role context moved to a header dropdown, so it is reachable on mobile in one tap.
  - react-markdown v9 compatible code/list handling (old `inline` / `node.parent` props are gone).
  - Accessibility: role="log" + aria-live, focus-visible rings, Escape closes menus, 44px touch targets.
  - Mobile: 100dvh, safe-area insets, 16px input text (stops iOS zoom), no horizontal overflow.
*/

/* -------------------------------------------------------
   CONFIG
------------------------------------------------------- */

const QUICK_QUESTIONS = [
  {
    label: "Tech stack",
    question: "What is your tech stack?",
    description: "Languages, cloud, data and AI technologies",
    icon: "⌘",
  },
  {
    label: "Data engineering",
    question: "Tell me about your Data engineering skills",
    description: "Pipelines, platforms, orchestration and analytics",
    icon: "◇",
  },
  {
    label: "Cloud engineering",
    question: "Tell me about your Cloud engineering skills",
    description: "AWS, GCP and cloud-native architecture",
    icon: "☁",
  },
  {
    label: "AI engineering",
    question: "Tell me about your AI engineering skills",
    description: "RAG, agents, LLMs and intelligent systems",
    icon: "✦",
  },
];

const ROLE_OPTIONS = [
  { name: "Recruiter", description: "Career & experience" },
  { name: "Engineer", description: "Technical depth" },
  { name: "Hiring Manager", description: "Capability & impact" },
  { name: "Student", description: "Learning & journey" },
];

const THINKING_MESSAGES = [
  "Searching knowledge base",
  "Reading engineering context",
  "Analysing the question",
  "Retrieving relevant experience",
  "Synthesising information",
  "Constructing response",
];

const AGENT_URL =
  import.meta?.env?.VITE_AGENT_FUNCTION_URL ||
  process.env.REACT_APP_AGENT_FUNCTION_URL ||
  "";

const THREAD_KEY = "portfolio_ai_thread_id";

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400..700;1,9..40,400..700&family=Source+Serif+4:ital,opsz,wght@0,8..60,400..700;1,8..60,400..700&display=swap";

/*
  TYPOGRAPHY
  UI chrome (buttons, menus, your messages): DM Sans, a geometric humanist sans with the
  friendly, rounded feel of Google's product UI (Gemini).
  Assistant answers: Source Serif 4, the closest open match to the serif Claude uses for
  its replies. Code: the platform monospace.

  Want the Gemini look instead? Change ANSWER_STYLE to "sans" and answers switch to
  DM Sans at a slightly smaller size. Nothing else needs touching.
*/
const ANSWER_STYLE = "serif"; // "serif" (Claude-style) | "sans" (Gemini-style)

const FONT_SANS =
  "'DM Sans', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji'";
const FONT_SERIF =
  "'Source Serif 4', 'Tiempos Text', Charter, 'Iowan Old Style', 'Palatino Linotype', Georgia, 'Times New Roman', serif";
const FONT_MONO =
  "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";

const ANSWER_FONT = ANSWER_STYLE === "serif" ? FONT_SERIF : FONT_SANS;
const ANSWER_TEXT =
  ANSWER_STYLE === "serif"
    ? "text-[17px] leading-[1.7]"
    : "text-[15.5px] leading-[1.75]";

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4CD8B8]/60";

/* Keyframes and a few things Tailwind utilities can't express cleanly. */
const GLOBAL_CSS = `
@keyframes sai-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
@keyframes sai-rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes sai-pop { from { opacity: 0; transform: scale(.96) translateY(-4px); } to { opacity: 1; transform: none; } }
.sai-shimmer {
  background: linear-gradient(90deg, #929AA6 0%, #EDEFF2 50%, #929AA6 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text; background-clip: text;
  color: transparent; -webkit-text-fill-color: transparent;
  animation: sai-shimmer 2.4s linear infinite;
}
.sai-rise { animation: sai-rise .28s cubic-bezier(.2,.7,.2,1) both; }
.sai-pop { animation: sai-pop .14s ease-out both; transform-origin: top; }
.sai-scroll { scrollbar-width: thin; scrollbar-color: rgba(237,239,242,.14) transparent; }
.sai-scroll::-webkit-scrollbar { width: 10px; }
.sai-scroll::-webkit-scrollbar-thumb { background: rgba(237,239,242,.12); border-radius: 999px; border: 3px solid transparent; background-clip: content-box; }
.sai-scroll::-webkit-scrollbar-thumb:hover { background: rgba(237,239,242,.22); background-clip: content-box; border: 3px solid transparent; }
.sai-md > :first-child { margin-top: 0; }
.sai-md > :last-child { margin-bottom: 0; }
@media (prefers-reduced-motion: reduce) {
  .sai-shimmer, .sai-rise, .sai-pop { animation: none !important; }
  .sai-shimmer { color: #929AA6; -webkit-text-fill-color: #929AA6; background: none; }
}
`;

/* -------------------------------------------------------
   HELPERS
------------------------------------------------------- */

function generateThreadId() {
  return `thread_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

function safeStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getOrCreateThreadId() {
  const store = safeStorage();
  const existing = store?.getItem(THREAD_KEY);
  if (existing) return existing;

  const created = generateThreadId();
  try {
    store?.setItem(THREAD_KEY, created);
  } catch {
    /* private mode: thread lives in memory only */
  }
  return created;
}

function textToRenderableMarkdown(text) {
  if (!text) return "";

  const normalized = text.replace(/\r\n/g, "\n").trim();

  const hasMarkdownListSyntax =
    /(^|\n)\s*[-*]\s+/.test(normalized) || /(^|\n)\s*\d+\.\s+/.test(normalized);

  const hasMarkdownHeadings = /(^|\n)\s*#{1,6}\s+/.test(normalized);

  const hasStructuredSections =
    normalized.includes("SUMMARY:") ||
    normalized.includes("CORE COMPETENCIES:") ||
    normalized.includes("STRATEGIC VALUE:") ||
    normalized.includes("MISSING CONTEXT:");

  if (hasMarkdownListSyntax || hasMarkdownHeadings || hasStructuredSections) {
    return normalized;
  }

  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length >= 3) {
    return lines.map((line) => `- ${line}`).join("\n");
  }

  return normalized;
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

/* Reveals text progressively; returns the visible slice. */
function useReveal(text, enabled) {
  const [count, setCount] = useState(enabled ? 0 : text.length);

  useEffect(() => {
    if (!enabled) {
      setCount(text.length);
      return undefined;
    }

    const total = text.length;
    const duration = Math.min(Math.max(total * 5, 500), 3200);
    const start = performance.now();
    let raf;

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      // ease-out so it settles instead of stopping abruptly
      const eased = 1 - Math.pow(1 - progress, 2);
      setCount(Math.floor(eased * total));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, enabled]);

  return text.slice(0, count);
}

/* -------------------------------------------------------
   ICONS (stroke icons, inherit currentColor)
------------------------------------------------------- */

function Icon({ children, size = 18, strokeWidth = 1.8 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const BotIcon = ({ size }) => (
  <Icon size={size}>
    <rect x="4" y="6" width="16" height="13" rx="3" />
    <path d="M9 6V4a3 3 0 0 1 6 0v2" />
    <path d="M8 12h.01M16 12h.01" />
    <path d="M9 16h6" />
  </Icon>
);

const SendIcon = () => (
  <Icon size={18} strokeWidth={2.4}>
    <path d="m5 12 7-7 7 7" />
    <path d="M12 19V5" />
  </Icon>
);

const StopIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <rect x="5" y="5" width="14" height="14" rx="3" />
  </svg>
);

const CopyIcon = () => (
  <Icon size={15}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </Icon>
);

const CheckIcon = ({ size = 15 }) => (
  <Icon size={size} strokeWidth={2.2}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Icon>
);

const RefreshIcon = () => (
  <Icon size={15}>
    <path d="M21 12a9 9 0 0 1-15.5 6.3L3 15.8" />
    <path d="M3 21v-5.2h5.2" />
    <path d="M3 12A9 9 0 0 1 18.5 5.7L21 8.2" />
    <path d="M21 3v5.2h-5.2" />
  </Icon>
);

const SidebarIcon = () => (
  <Icon size={20}>
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <path d="M9 4v16" />
  </Icon>
);

const PlusIcon = ({ size = 18 }) => (
  <Icon size={size} strokeWidth={2}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

const ChevronDown = () => (
  <Icon size={14} strokeWidth={2}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);

const CloseIcon = () => (
  <Icon size={18} strokeWidth={2}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);

const ArrowDownIcon = () => (
  <Icon size={16} strokeWidth={2.2}>
    <path d="M12 5v14" />
    <path d="m6 13 6 6 6-6" />
  </Icon>
);

/* -------------------------------------------------------
   MARKDOWN
------------------------------------------------------- */

function CodeBlock({ language, code }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (await copyText(code)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <div className="my-5 overflow-hidden rounded-xl border border-white/10 bg-[#0B0F15]">
      <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.03] py-1.5 pl-4 pr-2">
        <span
          className="text-xs text-[#929AA6]"
          style={{ fontFamily: FONT_MONO }}
        >
          {language || "code"}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className={`flex min-h-[32px] items-center gap-1.5 rounded-md px-2 text-xs text-[#929AA6] transition hover:bg-white/5 hover:text-[#EDEFF2] ${FOCUS}`}
        >
          {copied ? <CheckIcon size={14} /> : <CopyIcon />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="sai-scroll overflow-x-auto p-4">
        <code
          className="text-[13px] leading-6 text-[#EDEFF2]/90"
          style={{ fontFamily: FONT_MONO }}
        >
          {code}
        </code>
      </pre>
    </div>
  );
}

const markdownComponents = {
  h1: ({ children }) => (
    <h1 className="mb-3 mt-8 text-xl font-semibold tracking-tight text-[#EDEFF2]">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2.5 mt-7 text-lg font-semibold tracking-tight text-[#EDEFF2]">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-6 text-[15px] font-semibold text-[#4CD8B8]">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className={`mb-4 ${ANSWER_TEXT} text-[#EDEFF2]/90`}>{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mb-4 list-disc space-y-2 pl-6 marker:text-[#4CD8B8]">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-4 list-decimal space-y-2 pl-6 marker:font-medium marker:text-[#4CD8B8]">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li
      className={`pl-1 ${ANSWER_TEXT} text-[#EDEFF2]/90 [&>ol]:mb-0 [&>ol]:mt-2 [&>p]:mb-0 [&>ul]:mb-0 [&>ul]:mt-2`}
    >
      {children}
    </li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-white">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`rounded-sm text-[#4CD8B8] underline decoration-[#4CD8B8]/40 underline-offset-4 transition hover:decoration-[#4CD8B8] ${FOCUS}`}
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-5 border-l-2 border-[#4CD8B8]/60 pl-4 text-[#929AA6] [&>p]:mb-2 [&>p]:text-[#929AA6]">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-8 border-white/10" />,
  /* <pre> is rendered by CodeBlock, so pass children through. */
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const text = String(children).replace(/\n$/, "");
    const language = /language-([\w-]+)/.exec(className || "")?.[1];
    const isBlock = Boolean(language) || text.includes("\n");

    if (isBlock) return <CodeBlock language={language} code={text} />;

    return (
      <code
        className="rounded-md border border-white/10 bg-white/[0.06] px-1.5 py-0.5 text-[0.86em] text-[#4CD8B8]"
        style={{ fontFamily: FONT_MONO }}
      >
        {children}
      </code>
    );
  },
  /* Tables only render if you add remark-gfm; styled so they look right when you do. */
  table: ({ children }) => (
    <div className="sai-scroll my-5 overflow-x-auto rounded-xl border border-white/10">
      <table
        className="w-full border-collapse text-left text-sm"
        style={{ fontFamily: FONT_SANS }}
      >
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-white/10 bg-white/[0.04] px-4 py-2.5 font-semibold text-[#EDEFF2]">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-white/5 px-4 py-2.5 text-[#EDEFF2]/85">
      {children}
    </td>
  ),
};

/* -------------------------------------------------------
   SMALL UI PIECES
------------------------------------------------------- */

function Avatar({ size = "md" }) {
  const box = size === "sm" ? "h-7 w-7 rounded-lg" : "h-8 w-8 rounded-[10px]";
  return (
    <div
      className={`flex shrink-0 items-center justify-center border border-[#4CD8B8]/25 bg-[#12161D] text-[#4CD8B8] ${box}`}
    >
      <BotIcon size={size === "sm" ? 15 : 17} />
    </div>
  );
}

function IconButton({ label, onClick, children, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[#929AA6] transition hover:bg-white/[0.06] hover:text-[#EDEFF2] ${FOCUS} ${className}`}
    >
      {children}
    </button>
  );
}

function ThinkingIndicator() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((current) => (current + 1) % THINKING_MESSAGES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="sai-rise flex items-center gap-3" role="status">
      <Avatar size="sm" />
      <span className="sai-shimmer text-sm font-medium">
        {THINKING_MESSAGES[index]}…
      </span>
    </div>
  );
}

function RoleMenu({ role, setRole }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointer = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex min-h-[40px] items-center gap-2 rounded-xl px-3 text-sm text-[#EDEFF2] transition hover:bg-white/[0.06] ${FOCUS} ${
          open ? "bg-white/[0.06]" : ""
        }`}
      >
        <span className="hidden text-[#929AA6] sm:inline">Answering for</span>
        <span className="font-medium text-[#4CD8B8]">{role}</span>
        <span className="text-[#929AA6]">
          <ChevronDown />
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Response context"
          className="sai-pop absolute right-0 top-[calc(100%+6px)] z-50 w-64 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-white/10 bg-[#12161D] p-1.5 shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
        >
          {ROLE_OPTIONS.map((option) => {
            const active = option.name === role;
            return (
              <button
                key={option.name}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  setRole(option.name);
                  setOpen(false);
                }}
                className={`flex min-h-[48px] w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-white/[0.06] ${FOCUS}`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[#EDEFF2]">
                    {option.name}
                  </span>
                  <span className="block text-xs text-[#929AA6]">
                    {option.description}
                  </span>
                </span>
                {active && (
                  <span className="text-[#4CD8B8]">
                    <CheckIcon />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Reasoning({ intent, notes }) {
  const [open, setOpen] = useState(false);
  const hasNotes = Boolean(notes);

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-2">
        {intent && (
          <span className="rounded-full border border-[#E3A857]/25 bg-[#E3A857]/10 px-2.5 py-1 text-xs font-medium text-[#E3A857]">
            {intent}
          </span>
        )}
        {hasNotes && (
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            className={`flex min-h-[32px] items-center gap-1 rounded-lg px-2 text-xs text-[#929AA6] transition hover:bg-white/5 hover:text-[#EDEFF2] ${FOCUS}`}
          >
            How this was answered
            <span
              className={`transition-transform ${open ? "rotate-180" : ""}`}
            >
              <ChevronDown />
            </span>
          </button>
        )}
      </div>

      {hasNotes && open && (
        <p className="sai-rise mt-2 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-[13px] leading-6 text-[#929AA6]">
          {notes}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------
   MESSAGE
------------------------------------------------------- */

function ChatMessage({ message, isLast, explain, onRegenerate }) {
  const isUser = message.role === "user";
  const [copied, setCopied] = useState(false);

  const fullMarkdown = useMemo(
    () => (isUser ? "" : textToRenderableMarkdown(message.content)),
    [isUser, message.content],
  );

  const animate =
    !isUser && Boolean(message.animate) && !prefersReducedMotion();
  const visibleMarkdown = useReveal(fullMarkdown, animate);

  const handleCopy = async () => {
    if (await copyText(message.content)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  if (isUser) {
    return (
      <div className="sai-rise flex justify-end">
        <div className="max-w-[88%] whitespace-pre-wrap break-words rounded-3xl border border-white/10 bg-[#1A202A] px-4 py-2.5 text-[15.5px] leading-7 text-[#EDEFF2] sm:max-w-[80%]">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="sai-rise group flex gap-3 sm:gap-4">
      <div className="pt-0.5">
        <Avatar size="sm" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="sai-md break-words" style={{ fontFamily: ANSWER_FONT }}>
          <ReactMarkdown components={markdownComponents}>
            {visibleMarkdown}
          </ReactMarkdown>
        </div>

        {/* Actions: always on for touch, always on for the latest answer, hover elsewhere */}
        <div
          className={`-ml-2 mt-1 flex items-center gap-0.5 transition-opacity duration-200 ${
            isLast
              ? "opacity-100"
              : "opacity-100 md:opacity-0 md:focus-within:opacity-100 md:group-hover:opacity-100"
          }`}
        >
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy answer"
            className={`flex min-h-[36px] items-center gap-1.5 rounded-lg px-2.5 text-xs text-[#929AA6] transition hover:bg-white/[0.06] hover:text-[#EDEFF2] ${FOCUS}`}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied ? "Copied" : "Copy"}
          </button>

          {onRegenerate && (
            <button
              type="button"
              onClick={onRegenerate}
              aria-label="Regenerate answer"
              className={`flex min-h-[36px] items-center gap-1.5 rounded-lg px-2.5 text-xs text-[#929AA6] transition hover:bg-white/[0.06] hover:text-[#EDEFF2] ${FOCUS}`}
            >
              <RefreshIcon />
              Regenerate
            </button>
          )}
        </div>

        {explain && (message.intent || message.grounding_notes) && (
          <Reasoning intent={message.intent} notes={message.grounding_notes} />
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   SIDEBAR
------------------------------------------------------- */

function ChatSidebar({
  explain,
  setExplain,
  onQuickQuestion,
  onNewSession,
  mobileOpen,
  closeMobile,
  desktopOpen,
  closeDesktop,
}) {
  return (
    <>
      {/* Mobile scrim */}
      <div
        onClick={closeMobile}
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] transition-opacity duration-200 lg:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      <aside
        aria-label="Chat menu"
        className={`fixed inset-y-0 left-0 z-50 border-r border-white/10 bg-[#0B0F15] transition-[transform,width,visibility] duration-300 ease-out lg:static lg:z-auto lg:translate-x-0 lg:overflow-hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full max-lg:invisible"
        } ${
          desktopOpen ? "lg:w-[280px]" : "lg:invisible lg:w-0 lg:border-r-0"
        }`}
      >
        <div className="flex h-full w-[300px] max-w-[85vw] flex-col pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] lg:w-[280px] lg:max-w-none">
          {/* Brand */}
          <div className="flex items-center gap-3 px-4 pb-3 pt-4">
            <Avatar />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold text-[#EDEFF2]">
                Stephen AI
              </p>
              <p className="truncate text-xs text-[#929AA6]">
                Personal intelligence layer
              </p>
            </div>
            <IconButton
              label="Close menu"
              onClick={closeMobile}
              className="lg:hidden"
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              label="Collapse sidebar"
              onClick={closeDesktop}
              className="hidden lg:flex"
            >
              <SidebarIcon />
            </IconButton>
          </div>

          <div className="px-3 pb-2">
            <button
              type="button"
              onClick={() => {
                onNewSession();
                closeMobile();
              }}
              className={`flex min-h-[44px] w-full items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 text-sm font-medium text-[#EDEFF2] transition hover:border-[#4CD8B8]/30 hover:bg-[#4CD8B8]/[0.06] ${FOCUS}`}
            >
              <span className="text-[#4CD8B8]">
                <PlusIcon />
              </span>
              New conversation
            </button>
          </div>

          {/* Explore */}
          <nav className="sai-scroll min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-3">
            <p className="mb-1.5 px-3 text-xs font-medium text-[#929AA6]">
              Explore
            </p>

            <ul className="space-y-0.5">
              {QUICK_QUESTIONS.map((item) => (
                <li key={item.question}>
                  <button
                    type="button"
                    onClick={() => {
                      onQuickQuestion(item.question);
                      closeMobile();
                    }}
                    className={`flex min-h-[48px] w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-white/[0.05] ${FOCUS}`}
                  >
                    <span className="mt-0.5 w-5 shrink-0 text-center text-[15px] text-[#4CD8B8]">
                      {item.icon}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-[#EDEFF2]">
                        {item.label}
                      </span>
                      <span className="mt-0.5 block text-xs leading-5 text-[#929AA6]">
                        {item.description}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Settings */}
          <div className="border-t border-white/10 p-3">
            <div className="flex min-h-[52px] items-center justify-between gap-3 rounded-xl px-3">
              <div className="min-w-0">
                <p
                  className="text-sm font-medium text-[#EDEFF2]"
                  id="explain-label"
                >
                  Explainability
                </p>
                <p className="text-xs text-[#929AA6]">
                  Show how answers were made
                </p>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={explain}
                aria-labelledby="explain-label"
                onClick={() => setExplain(!explain)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${FOCUS} ${
                  explain ? "bg-[#4CD8B8]" : "bg-white/15"
                }`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-[#080B10] shadow transition-transform duration-200 ${
                    explain ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

/* -------------------------------------------------------
   EMPTY STATE
------------------------------------------------------- */

function EmptyState({ role, onPick }) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center py-8 text-center">
      <div className="sai-rise mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#4CD8B8]/25 bg-[#12161D] text-[#4CD8B8] shadow-[0_0_60px_rgba(76,216,184,0.14)]">
        <BotIcon size={28} />
      </div>

      <h1
        className="sai-rise text-[28px] font-semibold leading-tight tracking-tight text-[#EDEFF2] sm:text-4xl"
        style={{ animationDelay: "40ms" }}
      >
        What would you like to know about Stephen?
      </h1>

      <p
        className="sai-rise mx-auto mt-3 max-w-md text-[15px] leading-7 text-[#929AA6]"
        style={{ animationDelay: "80ms" }}
      >
        Ask about engineering experience, projects and approach. Answers are
        tailored for a {role.toLowerCase()}.
      </p>

      <div
        className="sai-rise mt-8 grid w-full grid-cols-1 gap-2.5 text-left sm:mt-10 sm:grid-cols-2"
        style={{ animationDelay: "120ms" }}
      >
        {QUICK_QUESTIONS.map((item) => (
          <button
            key={item.question}
            type="button"
            onClick={() => onPick(item.question)}
            className={`group flex min-h-[64px] items-start gap-3.5 rounded-2xl border border-white/10 bg-[#12161D]/70 p-3.5 transition hover:border-[#4CD8B8]/30 hover:bg-[#12161D] sm:p-4 ${FOCUS}`}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-[#080B10] text-[15px] text-[#4CD8B8]">
              {item.icon}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-[#EDEFF2]">
                {item.label}
              </span>
              <span className="mt-0.5 block text-[13px] leading-5 text-[#929AA6]">
                {item.description}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   COMPOSER
------------------------------------------------------- */

function Composer({
  input,
  setInput,
  onSend,
  onStop,
  loading,
  role,
  error,
  clearError,
  textareaRef,
  showJump,
  onJump,
}) {
  const canSend = input.trim().length > 0 && !loading;

  /* Auto-grow. useLayoutEffect avoids a one-frame flicker; also resets after send. */
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input, textareaRef]);

  const handleKeyDown = (event) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return; // IME
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSend) onSend(input);
    }
  };

  return (
    <div className="relative z-20 shrink-0 bg-gradient-to-t from-[#080B10] via-[#080B10] to-[#080B10]/0 pt-2">
      {/* Jump to latest */}
      <button
        type="button"
        onClick={onJump}
        aria-label="Scroll to latest message"
        tabIndex={showJump ? 0 : -1}
        className={`absolute -top-12 left-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full border border-white/15 bg-[#12161D] text-[#EDEFF2] shadow-lg transition duration-200 hover:bg-[#1A202A] ${FOCUS} ${
          showJump
            ? "opacity-100"
            : "pointer-events-none translate-y-2 opacity-0"
        }`}
      >
        <ArrowDownIcon />
      </button>

      <div className="mx-auto w-full max-w-3xl px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:px-5 md:px-8">
        {error && (
          <div
            role="alert"
            className="mb-2 flex items-start justify-between gap-3 rounded-xl border border-red-500/25 bg-red-500/[0.07] px-4 py-3 text-sm text-red-300"
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={clearError}
              aria-label="Dismiss error"
              className={`-mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md hover:bg-white/10 ${FOCUS}`}
            >
              <CloseIcon />
            </button>
          </div>
        )}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (canSend) onSend(input);
          }}
        >
          <div className="relative rounded-[26px] border border-white/[0.14] bg-[#12161D] shadow-[0_8px_30px_rgba(0,0,0,0.35)] transition-colors focus-within:border-[#4CD8B8]/45">
            <label htmlFor="stephen-ai-input" className="sr-only">
              Message Stephen AI
            </label>
            <textarea
              id="stephen-ai-input"
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Ask Stephen AI as a ${role.toLowerCase()}…`}
              enterKeyHint="send"
              autoComplete="off"
              /* 16px on phones stops iOS Safari zooming into the field */
              className="sai-scroll block max-h-[200px] min-h-[56px] w-full resize-none bg-transparent py-4 pl-5 pr-16 text-base leading-6 text-[#EDEFF2] outline-none placeholder:text-[#929AA6]/60 sm:text-[15px]"
              style={{ fontFamily: FONT_SANS }}
            />

            {loading ? (
              <button
                type="button"
                onClick={onStop}
                aria-label="Stop generating"
                title="Stop generating"
                className={`absolute bottom-2.5 right-2.5 flex h-10 w-10 items-center justify-center rounded-full bg-[#EDEFF2] text-[#080B10] transition hover:bg-white ${FOCUS}`}
              >
                <StopIcon />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!canSend}
                aria-label="Send message"
                title="Send"
                className={`absolute bottom-2.5 right-2.5 flex h-10 w-10 items-center justify-center rounded-full bg-[#4CD8B8] text-[#080B10] transition hover:brightness-110 active:scale-95 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/30 disabled:hover:brightness-100 ${FOCUS}`}
              >
                <SendIcon />
              </button>
            )}
          </div>
        </form>

        <p className="px-2 pb-1 pt-2 text-center text-[11px] leading-4 text-[#929AA6]/70">
          AI-generated. Verify important details.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   MAIN CHAT
------------------------------------------------------- */

const Chat = () => {
  const [role, setRole] = useState("Recruiter");
  const [explain, setExplain] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState("");
  const [error, setError] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [showJump, setShowJump] = useState(false);

  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const stickRef = useRef(true);
  const abortControllerRef = useRef(null);
  const textareaRef = useRef(null);

  const hasMessages = messages.length > 0;

  /* Setup: fonts, title, thread id, cleanup */
  useEffect(() => {
    document.title = "Stephen AI | Stephen Adegbile";
    setThreadId(getOrCreateThreadId());

    if (!document.querySelector("link[data-stephen-ai-fonts]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = FONT_HREF;
      link.setAttribute("data-stephen-ai-fonts", "true");
      document.head.appendChild(link);
    }

    return () => abortControllerRef.current?.abort();
  }, []);

  /* Escape closes the mobile drawer; lock body scroll while it's open */
  useEffect(() => {
    if (!mobileOpen) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  /* Scroll tracking: only auto-follow when the reader is at the bottom */
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickRef.current = distance < 96;
    setShowJump(distance > 240);
  };

  const scrollToBottom = useCallback((behavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  /* Content grows (new message, reveal animation, images): follow if sticky */
  useEffect(() => {
    const content = contentRef.current;
    // Empty state starts at the top; only follow the bottom once there is a conversation.
    if (!hasMessages || !content || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      if (stickRef.current) {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, [hasMessages]);

  const resetComposerHeight = () => {
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  /*
    regenerateAt: index of the assistant message to replace.
    When provided, the preceding user message is reused and everything from the
    old answer onwards is dropped, so the transcript never shows a duplicate question.
  */
  const sendMessage = async (rawPrompt, regenerateAt = null) => {
    const prompt = rawPrompt.trim();

    if (!prompt || loading) return;

    if (!AGENT_URL) {
      setError(
        "The AI backend URL is not configured. Set REACT_APP_AGENT_FUNCTION_URL (or VITE_AGENT_FUNCTION_URL).",
      );
      return;
    }

    setError("");
    setLoading(true);
    stickRef.current = true;

    if (regenerateAt !== null) {
      setMessages((previous) => previous.slice(0, regenerateAt));
    } else {
      setMessages((previous) => [
        ...previous,
        { id: `user_${Date.now()}`, role: "user", content: prompt },
      ]);
      setInput("");
      resetComposerHeight();
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(AGENT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: prompt,
          role: role.toLowerCase(),
          thread_id: threadId,
        }),
        signal: controller.signal,
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        /* non-JSON body; handled below */
      }

      if (!response.ok) {
        throw new Error(data?.error || `Request failed (${response.status}).`);
      }

      setMessages((previous) => [
        ...previous,
        {
          id: `assistant_${Date.now()}`,
          role: "assistant",
          content: data?.answer || "Sorry, I couldn't generate a response.",
          intent: data?.intent,
          grounding_notes: data?.grounding_notes,
          animate: true,
        },
      ]);
    } catch (err) {
      if (err.name === "AbortError") return;

      setMessages((previous) => [
        ...previous,
        {
          id: `error_${Date.now()}`,
          role: "assistant",
          content: `**Something went wrong**\n\n${err.message}\n\nCheck your connection and try again.`,
        },
      ]);
    } finally {
      if (abortControllerRef.current === controller) setLoading(false);
    }
  };

  const handleStop = () => {
    abortControllerRef.current?.abort();
    setLoading(false);
  };

  const handleNewSession = () => {
    abortControllerRef.current?.abort();

    const newThreadId = generateThreadId();
    try {
      safeStorage()?.setItem(THREAD_KEY, newThreadId);
    } catch {
      /* ignore */
    }

    setThreadId(newThreadId);
    setMessages([]);
    setInput("");
    setError("");
    setLoading(false);
    setShowJump(false);
    resetComposerHeight();
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const handleRegenerate = (assistantIndex) => {
    const userMessage = [...messages]
      .slice(0, assistantIndex)
      .reverse()
      .find((message) => message.role === "user");

    if (userMessage) sendMessage(userMessage.content, assistantIndex);
  };

  const toggleSidebar = () => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    if (isDesktop) setDesktopOpen((value) => !value);
    else setMobileOpen(true);
  };

  const lastIndex = messages.length - 1;

  return (
    <div
      className="flex h-screen w-full overflow-hidden bg-[#080B10] text-[#EDEFF2] antialiased [-moz-osx-font-smoothing:grayscale]"
      style={{
        display: "flex",
        width: "100%",
        height: "100dvh", // tracks mobile browser toolbars
        maxHeight: "100dvh",
        overflow: "hidden",
        fontFamily: FONT_SANS,
        fontFeatureSettings: "'kern' 1, 'liga' 1",
        fontOpticalSizing: "auto",
        textRendering: "optimizeLegibility",
        WebkitTextSizeAdjust: "100%",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <style>{GLOBAL_CSS}</style>

      <ChatSidebar
        explain={explain}
        setExplain={setExplain}
        onQuickQuestion={(question) => sendMessage(question)}
        onNewSession={handleNewSession}
        mobileOpen={mobileOpen}
        closeMobile={() => setMobileOpen(false)}
        desktopOpen={desktopOpen}
        closeDesktop={() => setDesktopOpen(false)}
      />

      <div
        role="main"
        className="relative"
        style={{
          display: "grid",
          gridTemplateRows: "auto minmax(0, 1fr) auto",
          flex: "1 1 0%",
          minWidth: 0,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Ambient background (decorative, behind everything) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_0%,rgba(76,216,184,0.08),transparent_28%),radial-gradient(circle_at_8%_100%,rgba(167,139,250,0.04),transparent_28%)]"
        />

        {/* Header */}
        <div
          role="banner"
          className="relative z-20 flex h-14 shrink-0 items-center justify-between gap-2 px-2 pt-[env(safe-area-inset-top)] sm:px-3 md:px-4"
        >
          <div className="flex min-w-0 items-center gap-1">
            <IconButton
              label="Open menu"
              onClick={toggleSidebar}
              className={desktopOpen ? "lg:hidden" : ""}
            >
              <SidebarIcon />
            </IconButton>

            <div className="flex min-w-0 items-center gap-2 pl-1">
              <span
                className={`truncate text-[15px] font-semibold text-[#EDEFF2] ${
                  desktopOpen ? "lg:hidden" : ""
                }`}
              >
                Stephen AI
              </span>
              <span
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#4CD8B8]/20 bg-[#4CD8B8]/[0.06] px-2 py-0.5 text-[11px] font-medium text-[#4CD8B8]"
                aria-label="Status: online"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#4CD8B8] shadow-[0_0_6px_rgba(76,216,184,0.9)]" />
                Online
              </span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <RoleMenu role={role} setRole={setRole} />
            <IconButton label="New conversation" onClick={handleNewSession}>
              <PlusIcon size={20} />
            </IconButton>
          </div>
        </div>

        {/* Conversation: a plain flex child that owns the scroll (no absolute positioning) */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="sai-scroll relative z-10 overflow-x-hidden overscroll-contain"
          style={{
            minHeight: 0,
            height: "100%",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          aria-label="Conversation"
        >
          <div
            ref={contentRef}
            className="mx-auto flex min-h-full w-full max-w-3xl flex-col px-4 pb-6 pt-4 sm:px-5 md:px-8"
          >
            {!hasMessages ? (
              <EmptyState
                role={role}
                onPick={(question) => sendMessage(question)}
              />
            ) : (
              <div className="w-full space-y-7 sm:space-y-8">
                {messages.map((message, index) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    isLast={index === lastIndex}
                    explain={explain}
                    onRegenerate={
                      message.role === "assistant" &&
                      index === lastIndex &&
                      !loading
                        ? () => handleRegenerate(index)
                        : null
                    }
                  />
                ))}

                {loading && <ThinkingIndicator />}
              </div>
            )}
          </div>
        </div>

        <Composer
          input={input}
          setInput={setInput}
          onSend={(value) => sendMessage(value)}
          onStop={handleStop}
          loading={loading}
          role={role}
          error={error}
          clearError={() => setError("")}
          textareaRef={textareaRef}
          showJump={showJump}
          onJump={() => scrollToBottom("smooth")}
        />
      </div>
    </div>
  );
};

export default Chat;
